import context, { ILookupService, ServiceModel, ServiceStatus, ServiceType } from '@truckdown/systems';
import { defineComponent, ref, Ref, onMounted } from 'vue';
import { ILocationNetworkLists, CreateLocationNetworkListModel, UpdateLocationNetworkListModel, LocationNetworkListModel } from '@truckdown/locations';
import { showMessage } from '../Utilities/utilities';
import validationError from '../Validation/Error/Error.vue';
import { NetworkListValidator } from './Validation/NetworkListValidator';

interface IProps {
    networkLists: LocationNetworkListModel[];
    locationId: string;
}

interface IData {
    isLoading: Ref<boolean>,
    pageCount: Ref<number>,
    resetList: Ref<boolean>,
    showDeleteModal: Ref<boolean>,
    showEditModal: Ref<boolean>,
    showAddModal: Ref<boolean>,
    selectableLists: Ref<ServiceModel[]>,
    editedList: Ref<CreateLocationNetworkListModel | undefined | UpdateLocationNetworkListModel>
    listToDelete: Ref<string | undefined>,
    networkLists: Ref<LocationNetworkListModel[]>,
    networkListValidator: Ref<any>,
}

const getData = function (props: IProps): IData {
    return {
        isLoading: ref<boolean>(false),
        pageCount: ref<number>(10),
        resetList: ref<boolean>(false),
        showDeleteModal: ref<boolean>(false),
        showEditModal: ref<boolean>(false),
        showAddModal: ref<boolean>(false),
        selectableLists: ref<ServiceModel[]>([]),
        editedList: ref <CreateLocationNetworkListModel | UpdateLocationNetworkListModel | undefined>(),
        listToDelete: ref<string | undefined>(),
        networkLists: ref<LocationNetworkListModel[]>(props.networkLists),
        networkListValidator: ref<any>(new NetworkListValidator(undefined)),
    };
}

const isNetworkListValid = function (data: IData): boolean {
    data.networkListValidator.value = new NetworkListValidator(data.editedList.value);
    return data.networkListValidator.value.validate();
}

const getMethods = function (props: IProps, data: IData, ctx: any) {

    const updateItem = function (result: LocationNetworkListModel) {
        if (data.editedList.value != undefined && result) {
            const index = data.networkLists.value.findIndex(i => i.id === (data.editedList.value as UpdateLocationNetworkListModel).id);
            if (index !== -1) {
                data.networkLists.value[index] = result;
            }
        }
    };

    const removeItem = function () {
        data.networkLists.value = data.networkLists.value.filter(item => item.id !== data.listToDelete.value);
    };

    return {
        getNetworkName: function (code: string) : string | undefined {
            return data.selectableLists.value.find(item => item.code === code)?.name;
        },
        openEditModal: function (list: LocationNetworkListModel) {
            data.showEditModal.value = true;
            data.editedList.value = { ...list, locationId: props.locationId } as UpdateLocationNetworkListModel;
            data.networkListValidator.value = new NetworkListValidator(data.editedList.value);
        },
        openAddModal: function () {
            data.showAddModal.value = true;
            data.editedList.value = {
                locationId: props.locationId,
                code: "",
                networkId: ""
            } as CreateLocationNetworkListModel;
            data.networkListValidator.value = new NetworkListValidator(data.editedList.value);
        },
        openDeleteModal: function (list: LocationNetworkListModel) {
            data.showDeleteModal.value = true;
            data.listToDelete.value = list.id;
        },
        closeEditModal: function () {
            data.showEditModal.value = false;
            data.showAddModal.value = false;
            data.editedList.value = undefined;
        },
        closeDeleteModal: function () {
            data.showDeleteModal.value = false;
            data.listToDelete.value = undefined;
        },
        confirmSave: async function () {
            if (data.showAddModal.value && data.editedList.value) {
                if (isNetworkListValid(data)) {
                    try {
                        const manager = await context.getService<ILocationNetworkLists>('ILocationNetworkLists');

                        let result = await manager.createNewLocationNetworkList(data.editedList.value as CreateLocationNetworkListModel);
                        if (result && result.ok && result.data) {
                            data.showAddModal.value = false;
                            data.networkLists.value.push(result.data);
                            data.editedList.value = undefined;
                            showMessage(`Listing network list was succesfully created.`, "success", true, true);
                        } else {
                            showMessage(`An error occurred while adding the network list.`, "danger", true, true);
                        }
                    } catch (error) {
                        console.error('Error adding location network lists:', error);
                    }
                }
            } else if (data.editedList.value) {
                if (isNetworkListValid(data)) {
                    try {
                        const manager = await context.getService<ILocationNetworkLists>('ILocationNetworkLists');
                        let result = await manager.updateLocationNetworkList(data.editedList.value as UpdateLocationNetworkListModel);
                        if (result && result.ok && result.data != undefined) {
                            updateItem(result.data);
                            data.editedList.value = undefined;
                            data.showEditModal.value = false;
                            showMessage(`Listing network list was succesfully updated.`, "success", true, true);
                        } else {
                            showMessage(`An error occurred while updating the network list.`, "danger", true, true);
                        }
                    } catch (error) {
                        console.error('Error updating location network lists:', error);
                    }
                }
            }
        },
        confirmDelete: async function () {
            if (data.listToDelete.value) {
                const manager = await context.getService<ILocationNetworkLists>('ILocationNetworkLists');
                try {
                    let result = await manager.removeLocationNetworkList(data.listToDelete.value, props.locationId);
                    if (result && result.ok) {
                        removeItem();
                        data.listToDelete.value = "";
                        data.showDeleteModal.value = false;
                        showMessage(`Listing network list was succesfully deleted.`, "success", true, true);
                    } else {
                        showMessage(`An error occurred while deleting the network list.`, "danger", true, true);
                    }
                } catch (error) {
                    console.error('Error deleting location network lists:', error);
                }
            } 
        }
    }
}

const getDealerNetworks = async function () {
    try {
        const lookupService = await context.getService<ILookupService>('ILookupService');
        let serviceLookupResult = await lookupService.getServices({
            types: [ServiceType.DealerNetwork],
            status: ServiceStatus.Active
        });
        return serviceLookupResult;
    } catch (err) {
        console.error(err)
        return [];
    }
};

export default defineComponent({
    name: "network-lists",
    emits: [],
    components: {
        "error": validationError
    },
    props: {
        networkLists: {
            type: Array as () => LocationNetworkListModel[],
            required: true
        },
        locationId: {
            type: String,
            required: true
        }
    },
    setup: function (props, ctx) {
        let data = getData(props);
        let methods = getMethods(props, data, ctx);

        onMounted(async () => {
            data.selectableLists.value = await getDealerNetworks();
        });

        return {
            ...data,
            ...methods
        };
    }
});